import React from 'react';
import { FormattedMessage } from 'react-intl';

import { Sidebar } from '@futrli/ui-components';
import Form from 'carbon-react/lib/components/form';
import Button from 'carbon-react/lib/components/button';
import Typography from 'carbon-react/lib/components/typography';
import Box from 'carbon-react/lib/components/box';

import { FiltersSidebarProps } from './types';
import { TextMatchFilterField } from './components/text-match-filter-field';
import { RangeFilterField } from './components/range-filter-field';
import { DateRangeFilterField } from './components/date-range-filter-field';
import { SelectFilterField } from './components/select-filter-field';

/**
 * Main component for the filters sidebar, rendering the appropriate filter fields based on the provided configuration and handling user interactions
 * @param isOpen - Whether the sidebar is open
 * @param onClose - Function to call when the sidebar is closed
 * @param onApply - Function to call when the filters are applied
 * @param handlers - Object containing handler functions for different filter field types
 * @param filterConfig - Array of filter field configurations
 * @param header - Optional custom header for the sidebar
 * @param applyLabel - Label for the apply button
 * @param cancelLabel - Label for the cancel button
 */
export const FiltersSidebar = ({
  isOpen,
  onClose,
  onApply,
  handlers,
  filterConfig,
  header,
  applyLabel,
  cancelLabel
}: FiltersSidebarProps) => {
  return (
    <Sidebar
      open={isOpen}
      onCancel={onClose}
      header={
        header || (
          <Typography variant="h2" fontWeight="700">
            <FormattedMessage id="predict.filters" defaultMessage="Filters" />
          </Typography>
        )
      }
      size="medium-large"
      headerPadding={{ p: '24px' }}
    >
      <Form
        stickyFooter
        leftSideButtons={<Button onClick={onClose}>{cancelLabel}</Button>}
        saveButton={
          <Button buttonType="primary" onClick={onApply}>
            {applyLabel}
          </Button>
        }
      >
        <Box>
          {filterConfig.map(config => {
            switch (config.type) {
              case 'textMatch':
                return (
                  <Box key={config.key} mb={3}>
                    <TextMatchFilterField config={config} onChange={handlers.handleTextMatchChange} />
                  </Box>
                );
              case 'range':
                return (
                  <RangeFilterField
                    key={config.key}
                    config={config}
                    onToggle={handlers.handleToggle}
                    onFromChange={handlers.handleRangeFromChange}
                    onToChange={handlers.handleRangeToChange}
                  />
                );
              case 'dateRange':
                return (
                  <DateRangeFilterField
                    key={config.key}
                    config={config}
                    onToggle={handlers.handleToggle}
                    onFromChange={handlers.handleDateRangeFromChange}
                    onToChange={handlers.handleDateRangeToChange}
                  />
                );
              case 'select':
                return <SelectFilterField key={config.key} config={config} onChange={handlers.handleSelectChange} />;
              default:
                return null;
            }
          })}
        </Box>
      </Form>
    </Sidebar>
  );
};
